import { useState } from "react";
import { motion } from "framer-motion";
import { MenuItem, BentoCard, PriceTable, OrderPizzaCTA } from "@/components/MenuComponents";
import { CustomizerModalRoot } from "@/components/Customizers";
import {
  PIZZA_SIZES,
  PRESENTACIONES,
  PIZZAS_COMBINADAS,
  PIZZAS_ESPECIALES,
  CUADRADA_MAXI,
  POLLO_ITEMS,
  CARNES_ITEMS,
  QUESOS_FUNDIDOS,
  PASTAS_ITEMS,
  SOPAS_ITEMS,
  ENSALADAS_ITEMS,
  BEBIDAS_ITEMS,
  POSTRES_ITEMS,
  MENU_INFANTIL,
  MENU_INFANTIL_IMAGE,
} from "@/data/menuData";

const SectionHeader = ({ title, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
    className="mb-6 md:mb-8"
  >
    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#2B3618] tracking-tight">
      {title}
    </h2>
    {subtitle && (
      <p className="font-['DM_Sans'] text-sm md:text-base text-[#4A4A4A] mt-2">{subtitle}</p>
    )}
    <div className="w-16 h-1 bg-[#C41E3A] rounded-full mt-3" />
  </motion.div>
);

const cuadradaMaxiMode = (name) => {
  if (name === "Cuadrada Combinada") return "cuadrada-combi";
  if (name === "Maxi Pizza Combinada") return "maxi-combi";
  if (name === "Cuadrada Especial") return "cuadrada-esp";
  if (name === "Maxi Pizza Especial") return "maxi-esp";
  return null;
};

export const MenuSections = () => {
  const [customizer, setCustomizer] = useState(null);

  const openPizza = (mode, initialFlavor) =>
    setCustomizer({ type: "pizza", mode, initialFlavor });
  const openSquare = (item) => {
    const mode = cuadradaMaxiMode(item.name);
    if (!mode) return;
    setCustomizer({ type: "pizza", mode, fixedItem: item });
  };
  const openItem = (item, category) =>
    setCustomizer({ type: "item", item, category });
  const close = () => setCustomizer(null);

  const renderPizzaList = (items, mode, label) =>
    items.map((item, i) =>
      item.featured && item.image ? (
        <BentoCard
          key={item.name}
          {...item}
          label={label}
          onAdd={() => openPizza(mode, item.name)}
        />
      ) : (
        <MenuItem
          key={item.name}
          {...item}
          index={i}
          onAdd={() => openPizza(mode, item.name)}
        />
      )
    );

  const renderItems = (items, category) =>
    items.map((item, i) =>
      item.featured && item.image ? (
        <BentoCard
          key={item.name}
          {...item}
          label="Destacado"
          onAdd={() => openItem(item, category)}
        />
      ) : (
        <MenuItem
          key={item.name}
          {...item}
          index={i}
          onAdd={() => openItem(item, category)}
        />
      )
    );

  return (
    <div className="bg-[#E8E8E8]">
      {/* Pizzas Combinadas */}
      <section id="pizzas-combinadas" data-testid="section-pizzas-combinadas" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <SectionHeader title="Pizzas Combinadas" subtitle="Elige tamaño y hasta 2 sabores mitad y mitad" />
        <PriceTable sizes={PIZZA_SIZES.combinadas} title="Precios por tamaño" />
        <PriceTable sizes={PIZZA_SIZES.orilla_queso.combinadas} title="Orilla de queso (costo extra)" />
        <div className="mb-4 flex justify-center md:justify-start">
          <OrderPizzaCTA label="Armar Pizza Combinada" onClick={() => openPizza("combinada")} />
        </div>
        <div className="mt-4">{renderPizzaList(PIZZAS_COMBINADAS, "combinada", "Favorita")}</div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-8 bg-[#556B2F]/10 rounded-xl p-5 border border-[#556B2F]/20"
        >
          <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.15em] font-bold text-[#556B2F] mb-3">
            Presentaciones
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {PRESENTACIONES.map((p) => (
              <div key={p.name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#556B2F] flex-shrink-0" />
                <span className="font-['DM_Sans'] text-sm text-[#1A1A1A]">
                  {p.name} — <span className={p.note === "Sin costo" ? "text-[#556B2F] font-semibold" : "text-[#C41E3A] font-semibold"}>{p.note}</span>
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Pizzas Especiales */}
      <section id="pizzas-especiales" data-testid="section-pizzas-especiales" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Pizzas Especiales" subtitle="Elige tamaño y hasta 2 sabores mitad y mitad" />
        <PriceTable sizes={PIZZA_SIZES.especiales} title="Precios por tamaño" />
        <div className="mb-4 flex justify-center md:justify-start">
          <OrderPizzaCTA label="Armar Pizza Especial" onClick={() => openPizza("especial")} />
        </div>
        <div className="mt-4">{renderPizzaList(PIZZAS_ESPECIALES, "especial", "Destacada")}</div>
      </section>

      {/* Cuadrada y Maxi */}
      <section id="cuadrada-maxi" data-testid="section-cuadrada-maxi" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Cuadrada y Maxi Pizza" subtitle="La cuadrada lleva 4 combinaciones, la maxi 6" />
        <div className="mt-4">
          {CUADRADA_MAXI.map((item, i) =>
            item.featured && item.image ? (
              <BentoCard
                key={item.name}
                {...item}
                label="Familiar"
                onAdd={() => openSquare(item)}
              />
            ) : (
              <MenuItem
                key={item.name}
                {...item}
                index={i}
                onAdd={() => openSquare(item)}
              />
            )
          )}
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-4 bg-white/50 rounded-lg p-4 border border-[#1A1A1A]/8"
        >
          <p className="font-['DM_Sans'] text-sm text-[#4A4A4A]">
            <span className="font-semibold text-[#1A1A1A]">Orilla de queso:</span> Cuadrada +${PIZZA_SIZES.orilla_queso.cuadrada} | Maxi Pizza +${PIZZA_SIZES.orilla_queso.maxi}
          </p>
        </motion.div>
      </section>

      {/* Pollo */}
      <section id="pollo" data-testid="section-pollo" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Pollo" />
        <div>{renderItems(POLLO_ITEMS, "Pollo")}</div>
      </section>

      {/* Carnes */}
      <section id="carnes" data-testid="section-carnes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Carnes" />
        <div>{renderItems(CARNES_ITEMS, "Carnes")}</div>
      </section>

      {/* Quesos Fundidos */}
      <section id="quesos-fundidos" data-testid="section-quesos-fundidos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Quesos Fundidos" />
        <div>{renderItems(QUESOS_FUNDIDOS, "Quesos Fundidos")}</div>
      </section>

      {/* Pastas */}
      <section id="pastas" data-testid="section-pastas" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Pastas" subtitle="Incluye orden de pan de ajo de la casa" />
        <div>{renderItems(PASTAS_ITEMS, "Pastas")}</div>
      </section>

      {/* Sopas */}
      <section id="sopas" data-testid="section-sopas" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Sopas" subtitle="Incluye orden de pan de ajo de la casa" />
        <div>{renderItems(SOPAS_ITEMS, "Sopas")}</div>
      </section>

      {/* Ensaladas */}
      <section id="ensaladas" data-testid="section-ensaladas" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Ensaladas" />
        <div>{renderItems(ENSALADAS_ITEMS, "Ensaladas")}</div>
      </section>

      {/* Bebidas */}
      <section id="bebidas" data-testid="section-bebidas" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Bebidas" />
        <div>{renderItems(BEBIDAS_ITEMS, "Bebidas")}</div>
      </section>

      {/* Postres */}
      <section id="postres" data-testid="section-postres" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Postres" />
        <div>{renderItems(POSTRES_ITEMS, "Postres")}</div>
      </section>

      {/* Menu Infantil */}
      <section id="menu-infantil" data-testid="section-menu-infantil" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 border-t border-[#1A1A1A]/8">
        <SectionHeader title="Menú Infantil" />
        <BentoCard name="Menú Infantil" description="Hamburguesa Kids, Nuggets, Salchipulpos - Incluye bebida" image={MENU_INFANTIL_IMAGE} label="Para los pequeños" />
        <div>{renderItems(MENU_INFANTIL, "Menú Infantil")}</div>
      </section>

      <CustomizerModalRoot state={customizer} onClose={close} />
    </div>
  );
};
